<template>
    <InfiniteScroller #default="{ listPortion }" :list="trailersFiltered">
        <div v-if="isFetching" class="l-padded">
            <VSpinner size="medium" :line-fg-color="spinnerColor" :speed="1" />
        </div>

        <div
            v-else-if="!trailersFiltered.length"
            class="l-padded l-inline l-center t-subtle t-small"
        >
            <span>
                {{ $t('shared.noSearchResult') }}
            </span>
        </div>

        <table v-else>
            <thead>
                <tr>
                    <th>
                        {{ $t('type') }}

                        <IconButton @click="handleSort('type')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'type' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('name') }}

                        <IconButton @click="handleSort('name')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'name' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('weight') }}

                        <IconButton @click="handleSort('weight')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'weight' ? sortDirection : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th>
                        {{ $t('assetType') }}

                        <IconButton @click="handleSort('assetTypeName')">
                            <SortArrowIcon
                                :direction="
                                    sortBy === 'assetTypeName'
                                        ? sortDirection
                                        : 0
                                "
                                width="10"
                                height="10"
                            />
                        </IconButton>
                    </th>

                    <th v-if="hasLsvaAdminAccess">
                        {{ $t('actions') }}
                    </th>
                </tr>
            </thead>

            <template v-for="item in listPortion">
                <tr :key="item.id">
                    <td>{{ $t(`shared.lsva.trailerTypes.${item.type}`) }}</td>

                    <td>{{ item.name }}</td>

                    <td>{{ formatWeight(item.weight) }} t</td>

                    <td>{{ item.assetTypeName }}</td>

                    <td v-if="hasLsvaAdminAccess">
                        <div
                            v-if="
                                item.permissions &&
                                    item.permissions.access &&
                                    item.permissions.access === 'WRITE'
                            "
                            class="action-group l-inline l-gap-1"
                        >
                            <IconButton
                                class="action-chip action-chip--neutral"
                                :title="$t('edit')"
                                @click="handleEdit(item)"
                            >
                                <PencilIcon />
                            </IconButton>
                        </div>
                    </td>
                </tr>
            </template>
        </table>
    </InfiniteScroller>
</template>

<script>
import { mapGetters } from 'vuex'
import { get } from 'lodash'
import VSpinner from 'vue-simple-spinner'

import { httpHelper } from '@/utils'
import IconButton from '@/components/IconButton'
import InfiniteScroller from '@/components/InfiniteScroller'
import LsvaTrailerEditModal from '@/components/lsva/LsvaTrailerEditModal'
import PencilIcon from '@/components/icons/PencilIcon'
import SortArrowIcon from '@/components/icons/SortArrowIcon'

const defaultSortColumn = 'name'
const defaultSortDirection = 1

export default {
    name: 'LsvaTrailersTable',
    components: {
        IconButton,
        InfiniteScroller,
        PencilIcon,
        SortArrowIcon,
        VSpinner,
    },
    props: {
        filterName: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            isFetching: false,
            sortBy: defaultSortColumn,
            sortDirection: defaultSortDirection,
            spinnerColor: process.env.VUE_APP_COLOR_PRIMARY,
            trailers: [],
        }
    },
    computed: {
        ...mapGetters('auth', ['hasLsvaAdminAccess']),
        ...mapGetters('tracker', ['assetTypesById']),
        trailersFiltered() {
            let result = this.trailers

            if (this.filterName) {
                const query = this.filterName.toLowerCase()
                result = result.filter(item =>
                    item.name.toLowerCase().includes(query)
                )
            }

            if (this.sortBy) {
                result = [...result].sort((a, b) => {
                    const aValue = get(a, this.sortBy)
                    const bValue = get(b, this.sortBy)

                    if (aValue === bValue) {
                        return 0
                    } else if (aValue == null) {
                        return 1
                    } else if (bValue == null) {
                        return -1
                    } else if (typeof aValue === 'string') {
                        return (
                            aValue.localeCompare(bValue, undefined, {
                                numeric: true,
                            }) * this.sortDirection
                        )
                    }

                    return aValue > bValue
                        ? this.sortDirection
                        : -this.sortDirection
                })
            }

            return result
        },
    },
    mounted() {
        this.fetchTrailers()
    },
    methods: {
        async fetchTrailers() {
            this.isFetching = true
            const { data } = await httpHelper.getRecursively(
                `lsva/trailers/?limit=${process.env.VUE_APP_LIMIT_RECORDS_PER_REQUEST}`
            )
            this.trailers = data.results.map(trailer => ({
                ...trailer,
                assetTypeName: this.assetTypesById[trailer.asset_type]?.name,
            }))
            this.isFetching = false
        },
        formatWeight(weight) {
            return weight ? (weight / 1000).toFixed(2) : weight
        },
        handleEdit(trailer) {
            this.$modal.show(
                LsvaTrailerEditModal,
                { trailer, onSubmit: this.fetchTrailers },
                { width: 400, height: '75%' }
            )
        },
        handleSort(column) {
            if (this.sortBy !== column || this.sortDirection !== 1) {
                this.sortBy = column
                this.sortDirection = 1
            } else if (this.sortDirection === 1) {
                this.sortDirection = -1
            }
        },
    },
}
</script>

<i18n>
{
    "en": {
        "actions": "Actions",
        "assetType": "Group",
        "edit": "Edit",
        "name": "Name",
        "type": "Type",
        "weight": "Weight"
    },
    "de": {
        "actions": "Aktionen",
        "assetType": "Gruppe",
        "edit": "Bearbeiten",
        "name": "Name",
        "type": "Typ",
        "weight": "Gewicht"
    },
    "fr": {
        "actions": "Actions",
        "assetType": "Groupe",
        "edit": "Editer",
        "name": "Nom",
        "type": "Type",
        "weight": "Poids"
    },
    "it": {
        "actions": "Azione",
        "assetType": "Gruppo",
        "edit": "Condizione",
        "name": "Nome",
        "type": "Typo",
        "weight": "Peso"
    }
}
</i18n>

<style lang="scss" scoped>
table {
    display: block;
    width: 100%;
    border-spacing: 0;
    font-size: 15px;
    color: rgba(0, 0, 0, 0.7);

    thead {
        position: sticky;
        top: 0;
        background-color: #fff;
        z-index: 1;

        .icon-button {
            margin-left: 5px;
        }
    }

    tr {
        &:not(:first-of-type) {
            td {
                border-top: $style-border;
            }
        }

        th {
            text-align: left;
            border-bottom: $style-border;
        }

        th,
        td {
            padding: 1rem;
            white-space: nowrap;

            &:first-child {
                padding-left: 2rem;
            }

            &:last-child {
                padding-right: 2rem;
            }

            &:not(:last-child) {
                width: 1%;
            }
        }
    }

    a {
        display: block;

        .icon-button {
            display: block;
        }
    }
}
</style>
